import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import type { TryOnFraming } from '../../types/tryOn';
import { t } from '../../utils/ui-strings';
import {
  EXPORT_JPEG_QUALITY,
  FEET_GUIDE,
  MAX_ZOOM,
  MIN_FACE_PX,
  type CropRect,
  type SourceSize,
  impliedFacePx,
  initialCrop,
  maxZoomLevel,
  outputSize,
  panCrop,
  ringGuideViewBox,
  setZoom,
  zoomCrop,
  zoomLevel,
} from './crop-geometry';

interface PhotoCropperProps {
  file: File;
  lang?: string;
  disabled?: boolean;
  framing?: TryOnFraming;
  onCropped: (file: File) => void;
  onCancel: () => void;
}

/** Förhandsvisningens buffert. CSS styr den renderade storleken. */
const PREVIEW_WIDTH = 640;
const PREVIEW_HEIGHT = 960;

function jpegName(originalName: string): string {
  const base = originalName.replace(/\.[^.]+$/, '') || 'photo';
  return `${base}.jpg`;
}

export function PhotoCropper({
  file,
  lang,
  disabled = false,
  framing = 'full',
  onCropped,
  onCancel,
}: PhotoCropperProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const bitmapRef = useRef<ImageBitmap | null>(null);
  const pointersRef = useRef(new Map<number, { x: number; y: number }>());
  const pinchDistanceRef = useRef<number | null>(null);
  const exportingRef = useRef(false);

  const [source, setSource] = useState<SourceSize | null>(null);
  const [crop, setCrop] = useState<CropRect | null>(null);
  const [failed, setFailed] = useState(false);

  // EXIF bakas in HÄR. Den exporterade JPEG:en bär ingen EXIF, så roteringen
  // måste ske vid avkodningen — annars blir serverns .rotate() verkningslös
  // och kunden hamnar liggande i genereringen.
  useEffect(() => {
    let cancelled = false;
    let bitmap: ImageBitmap | null = null;

    createImageBitmap(file, { imageOrientation: 'from-image' })
      .then((decoded) => {
        if (cancelled) {
          decoded.close();
          return;
        }
        bitmap = decoded;
        bitmapRef.current = decoded;
        const size = { width: decoded.width, height: decoded.height };
        setSource(size);
        setCrop(initialCrop(size, framing));
      })
      .catch(() => {
        if (cancelled) return;
        // Kan webbläsaren inte avkoda bilden faller vi tillbaka på det som
        // gällde före beskärningen: originalfilen går upp och servern beskär
        // den som förut. Att låsa kunden i en återvändsgränd är värre än en
        // obeställd inramning.
        onCropped(file);
      });

    return () => {
      cancelled = true;
      bitmap?.close();
      bitmapRef.current = null;
    };
  }, [file, framing]);

  // Ritas om vid varje ändring — enklare och mer förutsägbart än en
  // CSS-transform som måste hållas synkad med samma matematik.
  useEffect(() => {
    const canvas = canvasRef.current;
    const bitmap = bitmapRef.current;
    if (!canvas || !bitmap || !crop) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.drawImage(
      bitmap,
      crop.left,
      crop.top,
      crop.width,
      crop.height,
      0,
      0,
      canvas.width,
      canvas.height,
    );
  }, [crop]);

  /** Skärmpixlar → källpixlar. Beskärningen lever i källans koordinatsystem. */
  const toSourceDelta = useCallback(
    (deltaCssPx: number, axis: 'x' | 'y') => {
      const canvas = canvasRef.current;
      if (!canvas || !crop) return 0;
      const rect = canvas.getBoundingClientRect();
      const displayed = axis === 'x' ? rect.width : rect.height;
      const spanned = axis === 'x' ? crop.width : crop.height;
      if (displayed === 0) return 0;
      return (deltaCssPx * spanned) / displayed;
    },
    [crop],
  );

  const handlePointerDown = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled) return;
    event.currentTarget.setPointerCapture(event.pointerId);
    pointersRef.current.set(event.pointerId, { x: event.clientX, y: event.clientY });
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    if (disabled || !source || !crop) return;
    const pointers = pointersRef.current;
    const previous = pointers.get(event.pointerId);
    if (!previous) return;
    pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.size >= 2) {
      const [a, b] = Array.from(pointers.values());
      const distance = Math.hypot(a.x - b.x, a.y - b.y);
      const last = pinchDistanceRef.current;
      pinchDistanceRef.current = distance;
      if (last && last > 0) setCrop(zoomCrop(crop, source, distance / last));
      return;
    }

    // Dra bilden åt höger = flytta beskärningen åt vänster.
    setCrop(
      panCrop(
        crop,
        source,
        -toSourceDelta(event.clientX - previous.x, 'x'),
        -toSourceDelta(event.clientY - previous.y, 'y'),
      ),
    );
  };

  const endPointer = (event: ReactPointerEvent<HTMLCanvasElement>) => {
    pointersRef.current.delete(event.pointerId);
    if (pointersRef.current.size < 2) pinchDistanceRef.current = null;
  };

  // Wheel kopplas MANUELLT och icke-passivt. React registrerar onWheel som
  // passiv på rotnoden, och då är preventDefault() en tyst no-op: sidan
  // skrollar i stället för att bilden zoomar, utan att något syns i koden.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas || !source || !crop || disabled) return undefined;

    const onWheel = (event: WheelEvent) => {
      event.preventDefault();
      setCrop(zoomCrop(crop, source, event.deltaY < 0 ? 1.1 : 1 / 1.1));
    };

    canvas.addEventListener('wheel', onWheel, { passive: false });
    return () => canvas.removeEventListener('wheel', onWheel);
  }, [crop, source, disabled]);

  const handleConfirm = async () => {
    const bitmap = bitmapRef.current;
    if (!bitmap || !crop || disabled) return;
    // Dubbeltryck får inte ge två exporter. Knappens disabled-prop ägs av
    // föräldern och hinner inte uppdateras mellan de två klicken, så spärren
    // måste ligga här.
    if (exportingRef.current) return;
    exportingRef.current = true;

    try {
      const { width, height } = outputSize(crop);
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext('2d');
      if (!ctx) throw new Error('no 2d context');
      ctx.drawImage(bitmap, crop.left, crop.top, crop.width, crop.height, 0, 0, width, height);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/jpeg', EXPORT_JPEG_QUALITY);
      });
      if (!blob) throw new Error('toBlob returned null');

      onCropped(new File([blob], jpegName(file.name), { type: 'image/jpeg' }));
    } catch {
      setFailed(true);
    } finally {
      exportingRef.current = false;
    }
  };

  if (failed) {
    return (
      <section className="space-y-3">
        <p className="rounded-lg bg-red-50 p-3 text-sm text-red-700">
          {t('try_on_crop_failed', lang)}
        </p>
        <button
          type="button"
          onClick={onCancel}
          className="w-full rounded-xl border border-brand-blue/30 px-4 py-3 text-sm font-semibold text-brand-blue"
        >
          {t('try_on_crop_change', lang)}
        </button>
      </section>
    );
  }

  const currentZoom = source && crop ? zoomLevel(crop, source) : 1;
  const ring = ringGuideViewBox(currentZoom);
  const reachableZoom = source ? maxZoomLevel(source) : MAX_ZOOM;
  const feet = framing === 'feet';
  const faceTooSmall =
    !feet && source !== null && crop !== null && impliedFacePx(crop, source) < MIN_FACE_PX;

  return (
    <section className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold text-brand-blue">
          {t(feet ? 'try_on_feet_crop_title' : 'try_on_crop_title', lang)}
        </h3>
        <p className="mt-1 text-sm text-text-grey">
          {t(feet ? 'try_on_feet_crop_help' : 'try_on_crop_help', lang)}
        </p>
      </div>

      <div className="relative mx-auto aspect-[2/3] max-h-[55vh] w-full max-w-xs overflow-hidden rounded-2xl bg-brand-beige-tint-1">
        <canvas
          ref={canvasRef}
          width={PREVIEW_WIDTH}
          height={PREVIEW_HEIGHT}
          className="h-full w-full touch-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={endPointer}
          onPointerCancel={endPointer}
        />
        {/* viewBox 2x3 = ramens format, så ringens tal blir upplösningsoberoende. */}
        <svg
          viewBox="0 0 2 3"
          preserveAspectRatio="none"
          className="pointer-events-none absolute inset-0 h-full w-full"
        >
          {feet ? (
            <rect
              x={FEET_GUIDE.x}
              y={FEET_GUIDE.y}
              width={FEET_GUIDE.width}
              height={FEET_GUIDE.height}
              rx={FEET_GUIDE.rx}
              fill="none"
              stroke="#ffffff"
              strokeWidth={0.02}
              strokeDasharray="0.06 0.04"
            />
          ) : (
            <circle
              cx={ring.cx}
              cy={ring.cy}
              r={ring.r}
              fill="none"
              stroke="#ffffff"
              strokeWidth={0.02}
              strokeDasharray="0.06 0.04"
            />
          )}
        </svg>
      </div>

      {faceTooSmall ? (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-900">
          {t('try_on_crop_face_small', lang)}
        </p>
      ) : null}

      <label className="flex items-center gap-3">
        <span className="text-xs text-text-grey">{t('try_on_crop_zoom', lang)}</span>
        <input
          type="range"
          className="h-1 w-full accent-brand-blue"
          min={1}
          max={reachableZoom}
          step={0.01}
          value={currentZoom}
          disabled={disabled || !crop || reachableZoom <= 1}
          aria-label={t('try_on_crop_zoom', lang)}
          onChange={(event) => {
            if (!source || !crop) return;
            setCrop(setZoom(crop, source, Number(event.target.value)));
          }}
        />
      </label>

      <div className="flex flex-col gap-2 sm:flex-row">
        <button
          type="button"
          disabled={disabled || !crop}
          onClick={() => source && setCrop(initialCrop(source, framing))}
          className="rounded-xl border border-brand-blue/30 px-4 py-3 text-sm font-semibold text-brand-blue disabled:opacity-50 sm:w-1/3"
        >
          {t('try_on_crop_reset', lang)}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-xl border border-brand-blue/30 px-4 py-3 text-sm font-semibold text-brand-blue disabled:opacity-50 sm:w-1/3"
        >
          {t('try_on_crop_change', lang)}
        </button>
        <button
          type="button"
          disabled={disabled || !crop}
          onClick={handleConfirm}
          className="rounded-xl bg-brand-blue px-4 py-3 text-sm font-semibold text-white transition hover:bg-brand-secondary-blue disabled:opacity-50 sm:w-1/3"
        >
          {t('try_on_crop_confirm', lang)}
        </button>
      </div>
    </section>
  );
}
